import { useState, type ReactNode } from "react";
import css from "./CollapsibleSection.module.css";

/**
 * A page section that folds away, remembering whether it was folded.
 *
 * Built on native `<details>`/`<summary>` — the same idiom the skipped-address
 * list and the coverage grid already use. The browser gives keyboard operation,
 * the disclosure role and find-in-page expansion for free; a hand-rolled
 * div+onClick gives none of those and has to reimplement all three.
 *
 * **State is remembered per section, not per visit.** A long report is read by
 * someone hunting for one number, and they hunt for the same number again
 * tomorrow: a fold that resets on every navigation costs the same clicks every
 * time. The key is namespaced by `id`, so two pages can both have a "wms"
 * section without sharing a preference.
 *
 * Everything renders expanded on paper regardless — see the print rule in the
 * stylesheet. A collapsed section is a reading convenience, never a decision to
 * omit something from the printed report.
 */

const KEY_PREFIX = "twin.section.";

/** Storage is best-effort: private-mode Safari throws on setItem, and a fold
 *  preference is never worth breaking a report render over. */
function readOpen(id: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(KEY_PREFIX + id);
    return v == null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

function writeOpen(id: string, open: boolean): void {
  try {
    localStorage.setItem(KEY_PREFIX + id, open ? "1" : "0");
  } catch {
    /* preference not persisted; the section still works this visit */
  }
}

export interface CollapsibleSectionProps {
  /** Stable storage key. Changing it forgets the reader's preference. */
  id: string;
  title: string;
  /** Used until the reader folds it once; thereafter their choice wins. */
  defaultOpen?: boolean;
  /** Short count/status shown beside the title, e.g. "256 endereços". */
  badge?: ReactNode;
  className?: string;
  "data-testid"?: string;
  children: ReactNode;
}

export function CollapsibleSection({
  id, title, defaultOpen = true, badge, className, children, ...rest
}: CollapsibleSectionProps) {
  // Lazy init: reading storage on every render would be wasted work, and
  // react-hooks/exhaustive-deps is an error in this repo (ADR 136).
  const [open, setOpen] = useState(() => readOpen(id, defaultOpen));

  return (
    <details
      className={className ? `${css.section} ${className}` : css.section}
      open={open}
      onToggle={(e) => {
        const next = e.currentTarget.open;
        if (next === open) return;   // Chrome fires toggle on mount too
        setOpen(next);
        writeOpen(id, next);
      }}
      data-testid={rest["data-testid"]}
    >
      <summary className={css.summary}>
        <span className={css.chevron} aria-hidden="true" />
        <h2 className={css.title}>{title}</h2>
        {badge != null && <span className={css.badge}>{badge}</span>}
      </summary>
      <div className={css.body}>{children}</div>
    </details>
  );
}
